/**
 * Compute forecast values for a numeric series.
 * Returns an array of forecasted values.
 */
function _solve3(A, b) {
  // Solve a 3x3 linear system via Gaussian elimination. Returns [x0,x1,x2] or null.
  const m = [[...A[0], b[0]], [...A[1], b[1]], [...A[2], b[2]]];
  for (let col = 0; col < 3; col++) {
    let piv = col;
    for (let r = col + 1; r < 3; r++) if (Math.abs(m[r][col]) > Math.abs(m[piv][col])) piv = r;
    if (Math.abs(m[piv][col]) < 1e-12) return null;
    [m[col], m[piv]] = [m[piv], m[col]];
    for (let r = 0; r < 3; r++) {
      if (r === col) continue;
      const f = m[r][col] / m[col][col];
      for (let c = col; c < 4; c++) m[r][c] -= f * m[col][c];
    }
  }
  return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
}

export function computeForecastValues(vals, method, periods, seasonLength) {
  const n = vals.length;
  if (n === 0) return [];
  const forecastVals = [];

  if (method === 'linear') {
    const xMean = (n - 1) / 2;
    const yMean = vals.reduce((s, v) => s + v, 0) / n;
    let num = 0, den = 0;
    vals.forEach((y, i) => { num += (i - xMean) * (y - yMean); den += (i - xMean) * (i - xMean); });
    const slope = den !== 0 ? num / den : 0;
    const intercept = yMean - slope * xMean;
    for (let i = 0; i < periods; i++) forecastVals.push(intercept + slope * (n + i));
  } else if (method === 'exponential') {
    const alpha = 0.3, beta = 0.1;
    let level = vals[0], trend = n > 1 ? vals[1] - vals[0] : 0;
    for (let i = 1; i < n; i++) {
      const newLevel = alpha * vals[i] + (1 - alpha) * (level + trend);
      trend = beta * (newLevel - level) + (1 - beta) * trend;
      level = newLevel;
    }
    for (let i = 1; i <= periods; i++) forecastVals.push(level + trend * i);
  } else if (method === 'seasonal_naive') {
    const season = seasonLength || 12;
    for (let i = 0; i < periods; i++) {
      const idx = n - season + (i % season);
      forecastVals.push(idx >= 0 && idx < n ? vals[idx] : vals[n - 1]);
    }
  } else if (method === 'auto_ets') {
    const season = Math.min(seasonLength || 12, Math.floor(n / 2));
    const alpha = 0.3, beta = 0.05, gamma = 0.1;
    let level = vals.slice(0, season).reduce((s, v) => s + v, 0) / season;
    let trend = season >= 2 ? (vals.slice(season, season * 2).reduce((s, v) => s + v, 0) / season - level) / season : 0;
    const seasonal = vals.slice(0, season).map(v => v - level);
    for (let i = season; i < n; i++) {
      const si = i % season;
      const newLevel = alpha * (vals[i] - seasonal[si]) + (1 - alpha) * (level + trend);
      trend = beta * (newLevel - level) + (1 - beta) * trend;
      seasonal[si] = gamma * (vals[i] - newLevel) + (1 - gamma) * seasonal[si];
      level = newLevel;
    }
    for (let i = 1; i <= periods; i++) forecastVals.push(level + trend * i + seasonal[(n + i - 1) % season]);
  } else if (method === 'auto_arima') {
    const diffs = vals.slice(1).map((v, i) => v - vals[i]);
    const dMean = diffs.reduce((s, v) => s + v, 0) / diffs.length;
    let arNum = 0, arDen = 0;
    for (let i = 1; i < diffs.length; i++) {
      arNum += (diffs[i] - dMean) * (diffs[i - 1] - dMean);
      arDen += (diffs[i - 1] - dMean) * (diffs[i - 1] - dMean);
    }
    const phi = arDen !== 0 ? Math.max(-0.95, Math.min(0.95, arNum / arDen)) : 0;
    let lastDiff = diffs[diffs.length - 1] || 0;
    let lastVal = vals[n - 1];
    for (let i = 0; i < periods; i++) {
      const nextDiff = dMean + phi * (lastDiff - dMean);
      lastVal = lastVal + nextDiff;
      forecastVals.push(lastVal);
      lastDiff = nextDiff;
    }
  } else if (method === 'prophet') {
    const xMean = (n - 1) / 2;
    const yMean = vals.reduce((s, v) => s + v, 0) / n;
    let num = 0, den = 0;
    vals.forEach((y, i) => { num += (i - xMean) * (y - yMean); den += (i - xMean) * (i - xMean); });
    const slope = den !== 0 ? num / den : 0;
    const intercept = yMean - slope * xMean;
    const season = Math.min(seasonLength || 12, Math.floor(n / 2));
    const seasonalPattern = new Array(season).fill(0);
    const seasonCount = new Array(season).fill(0);
    vals.forEach((v, i) => {
      const detrended = v - (intercept + slope * i);
      seasonalPattern[i % season] += detrended;
      seasonCount[i % season]++;
    });
    seasonalPattern.forEach((_, i) => { if (seasonCount[i] > 0) seasonalPattern[i] /= seasonCount[i]; });
    for (let i = 0; i < periods; i++) {
      forecastVals.push(intercept + slope * (n + i) + seasonalPattern[(n + i) % season]);
    }
  } else if (method === 'moving_average') {
    const k = Math.min(3, n);
    const avg = vals.slice(n - k).reduce((s, v) => s + v, 0) / k;
    for (let i = 0; i < periods; i++) forecastVals.push(avg);
  } else if (method === 'weighted_ma') {
    const k = Math.min(4, n);
    const last = vals.slice(n - k);
    let wsum = 0, vsum = 0;
    last.forEach((v, i) => { const w = i + 1; wsum += w; vsum += w * v; });
    const wavg = wsum ? vsum / wsum : (vals[n - 1] || 0);
    for (let i = 0; i < periods; i++) forecastVals.push(wavg);
  } else if (method === 'damped_trend') {
    const alpha = 0.3, beta = 0.1, phi = 0.9;
    let level = vals[0], trend = n > 1 ? vals[1] - vals[0] : 0;
    for (let i = 1; i < n; i++) {
      const newLevel = alpha * vals[i] + (1 - alpha) * (level + phi * trend);
      trend = beta * (newLevel - level) + (1 - beta) * phi * trend;
      level = newLevel;
    }
    let cum = 0;
    for (let i = 1; i <= periods; i++) { cum += Math.pow(phi, i); forecastVals.push(level + cum * trend); }
  } else if (method === 'drift') {
    const driftRate = n > 1 ? (vals[n - 1] - vals[0]) / (n - 1) : 0;
    for (let i = 1; i <= periods; i++) forecastVals.push(vals[n - 1] + driftRate * i);
  } else if (method === 'quadratic') {
    // Least squares fit of y = a + b*x + c*x^2
    let S0 = n, S1 = 0, S2 = 0, S3 = 0, S4 = 0, T0 = 0, T1 = 0, T2 = 0;
    for (let i = 0; i < n; i++) { const x = i, y = vals[i], x2 = x * x; S1 += x; S2 += x2; S3 += x2 * x; S4 += x2 * x2; T0 += y; T1 += x * y; T2 += x2 * y; }
    const sol = _solve3([[S0, S1, S2], [S1, S2, S3], [S2, S3, S4]], [T0, T1, T2]);
    if (sol) {
      const [a, b, c] = sol;
      for (let i = 0; i < periods; i++) { const x = n + i; forecastVals.push(a + b * x + c * x * x); }
    }
  } else if (method === 'hw_multiplicative') {
    const season = Math.min(seasonLength || 12, Math.floor(n / 2)) || 1;
    const alpha = 0.3, beta = 0.05, gamma = 0.1;
    let level = vals.slice(0, season).reduce((s, v) => s + v, 0) / season;
    if (level === 0) level = 1e-6;
    let trend = season >= 2 ? (vals.slice(season, season * 2).reduce((s, v) => s + v, 0) / season - level) / season : 0;
    const seasonal = vals.slice(0, season).map(v => (level !== 0 ? v / level : 1));
    for (let i = season; i < n; i++) {
      const si = i % season;
      const s = seasonal[si] || 1;
      const newLevel = alpha * (vals[i] / (s || 1)) + (1 - alpha) * (level + trend);
      trend = beta * (newLevel - level) + (1 - beta) * trend;
      seasonal[si] = gamma * (vals[i] / (newLevel || 1e-6)) + (1 - gamma) * s;
      level = newLevel;
    }
    for (let i = 1; i <= periods; i++) forecastVals.push((level + trend * i) * (seasonal[(n + i - 1) % season] || 1));
  } else if (method === 'theta') {
    // Theta(2): SES level + half of the linear trend slope.
    const xMean = (n - 1) / 2;
    const yMean = vals.reduce((s, v) => s + v, 0) / n;
    let num = 0, den = 0;
    vals.forEach((y, i) => { num += (i - xMean) * (y - yMean); den += (i - xMean) * (i - xMean); });
    const slope = den !== 0 ? num / den : 0;
    const alpha = 0.3;
    let level = vals[0];
    for (let i = 1; i < n; i++) level = alpha * vals[i] + (1 - alpha) * level;
    for (let i = 1; i <= periods; i++) forecastVals.push(level + 0.5 * slope * i);
  }

  // Fallback
  if (forecastVals.length === 0) {
    const lastVal = vals[n - 1] || 0;
    for (let i = 0; i < periods; i++) forecastVals.push(lastVal);
  }

  return forecastVals;
}

/**
 * Compute confidence interval width for forecast points.
 */
export function computeForecastCI(vals, forecastIndex, totalOriginal) {
  const n = totalOriginal;
  const mean = vals.reduce((s, v) => s + v, 0) / n;
  const residuals = vals.map(v => v - mean);
  const stdErr = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / Math.max(n - 2, 1));
  return stdErr * 1.96 * Math.sqrt(1 + (forecastIndex + 1) / n);
}
